import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { Subscription } from 'rxjs';
import { DesktopNotesDataService } from 'modules/desktop/services/desktop-notes-data.service';
import { DesktopDrawingsDataService } from 'modules/desktop/services/desktop-drawings-data.service';
import { DesktopUserPreferencesService } from 'modules/desktop/services/desktop-user-preferences-data.service';
import { HelperService } from 'modules/desktop/services/helper.service';
import { DesktopGlobalStateService } from 'modules/desktop/services/desktop-global-state.service';
import { XAPIHelperService } from 'modules/foc/services/xAPI-helper.service';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';
import DesktopConfig from 'modules/desktop/services/config';

@Injectable()
export class DesktopEBookVMService {

    userId; // gigya user id of user.
    bookCode; // student book code of current book.
    spaceId; // used to store the current space of user.
    bundleCode; // used to store the bundle code of current book.
    vmActive = undefined; // VM instance of current user-book combination.
    isBookDownloaded = false; // flag to check if current book is downloaded by the user.

    // used to store the data fetched from local DB.
    localData: any = {
        notesData: [],
        drawingsData: [],
        preferencesData: []
    }

    // used to store the data fetched from server.
    serverData: any = {
        notesData: [],
        drawingsData: [],
        preferencesData: []
    }

    serverCopy;

    timerConfig = {
        timerDelay: 1000 * 60 * 5, // 1000*60*5
        isTimerActive: false,
        timerReference: undefined
    }

    static USER_DATA_TYPE = {
        NOTES: "notes",
        DRAWINGS: "drawings",
        PREFERENCES: {
            LAST_PAGE: "last_page"
        }
    }
    isOnline: boolean = false;
    networkStatusSubscription: Subscription;

    constructor(
        private networkService: NetwordDetectionService,
        private desktopNotesDataService: DesktopNotesDataService,
        private desktopDrawingsDataService: DesktopDrawingsDataService,
        private desktopUserPreferencesService: DesktopUserPreferencesService,
        private helperService: HelperService,
        private desktopGlobalStateService: DesktopGlobalStateService,
        private apiGatewayHelperService: ApiGatewayHelperService,
        private xAPIHelperService: XAPIHelperService,
    ) {
        this.xapiSuccessCallback = this.xapiSuccessCallback.bind(this);
    }

    /**
     * function to initialize the VM service.
     * triggered when the book is loaded.
     * @param userId - current user's gigya id
     * @param bookCode - current book's code
     * 
     * 
     */
    public async initializeVMService(userId, bookCode, spaceId, bundleCode, bookDownloadInfo) {

        // do not initialize the VM service if the app is not launched on Desktop.
        if (!environment.isDesktopApp) {
            return;
        }

        // initialize network subscription service to check for online and offline chnages
        this.isOnline = await this.networkService.isOnline(true);
        this.networkStatusSubscription = this.networkService.monitorStatus().subscribe(async (isOnline) => {
            if (this.isOnline != isOnline) {
                this.isOnline = isOnline;
            }
        });


        // find and update if the current book has been downloaded by the current user or not.
        this.isBookDownloaded = bookDownloadInfo

        // return if vm instance already exists or app not launched in desktop app or book not downloaded by user.
        if (this.isBookDownloaded && !this.isVMActive(userId, bookCode)) {

            this.userId = userId;
            this.bookCode = bookCode;
            this.spaceId = spaceId;
            this.bundleCode = bundleCode;
            this.setActiveVM(userId, bookCode);

            
            let fetchDataPromisesArray = [];
            // get all DB data in the vm.
            fetchDataPromisesArray.push(this._getAllDBData(this.userId, this.bookCode));

            // check if book has been launched before using global service.
            let wasDataFetchedOnce = this.desktopGlobalStateService.getState({ propName: DesktopGlobalStateService.STATE_PROPS.EBOOK_USER_DATA, ebookCode: this.bookCode });
            
            // get all server data to sync in VM.
            if(!wasDataFetchedOnce && this.isOnline) {
                fetchDataPromisesArray.push(this._getAllserverData());
            }
            await Promise.all(fetchDataPromisesArray);
            
            // if book is launched for first time and app is in online mode
            if (!wasDataFetchedOnce && this.isOnline) {
                this.desktopGlobalStateService.updateState(DesktopGlobalStateService.STATE_PROPS.EBOOK_USER_DATA, this.bookCode);
                this._completeSyncLocalDataWithServer(true);
            }

        }

    }

    /**
     * return if the vm is already running for existing book and user.
     * @param userId - current user's gigya id
     * @param bookCode - current book's code
     */
    public isVMActive(userId, bookCode) {
        return this.vmActive == `${userId}-${bookCode}`;
    }

    /**
     * set the vm instance for current book and user.
     * @param userId - current user's gigya id
     * @param bookCode - current book's code
     */
    public setActiveVM(userId, bookCode) {
        this.vmActive = `${userId}-${bookCode}`;
    }

    /**
     * function to return the active VM.
     */
    public getActiveVM() {
        return this.vmActive;
    }

    /**
     * used to reset current VM instance.
     * triggered when user has navigated away from ebook.
     */
    public resetVM() {
        this.vmActive = undefined;
        this.localData = {};
        this.serverData = {};
        this.serverCopy = undefined;
        this.userId = undefined;
        this.bookCode = undefined;
        this.spaceId = undefined;
        this.bundleCode = undefined;
        this.isBookDownloaded = undefined;
        this.xAPIHelperService.unsubscribe('success', this.xapiSuccessCallback);

    }

    /**
     * check if current user has downloaded the current book.
     */
    private _checkIfCurrentBookIsDownloadedByUser(userId, downloadInfo) {
        if (downloadInfo && downloadInfo.usersWithAccess.find(user => user == userId)) {
            this.isBookDownloaded = true;
        } else {
            this.isBookDownloaded = false;
        }

    }

    // sync user data from download service in case of auto-update.
    async syncUserData(userId, bookCode, spaceId) {

        this.userId = userId;
        this.bookCode = bookCode;

        // initialize network subscription service to check for online and offline chnages
        this.isOnline = await this.networkService.isOnline(true);
        this.networkStatusSubscription = this.networkService.monitorStatus().subscribe(async (isOnline) => {
            if (this.isOnline != isOnline) {
                this.isOnline = isOnline;
            }
        });

        let routePrefix = this.apiGatewayHelperService.getApiGatewayPrefixUrl();
        if (environment.isDesktopApp) {
            routePrefix = DesktopConfig.app.api.baseUrl + routePrefix;
        }
        let serverRoutePrefix = routePrefix.substr(0, routePrefix.length - 1);

        //instantiate xAPIAnalytics only once for a book launch
        this.xAPIHelperService.setXAPIAnalyticsObject({
            serverRoutePrefix: serverRoutePrefix,
            dlsProductCode: bookCode,
            spaceId: spaceId,
            userId: userId
        });

        await this._getAllDBData(this.userId, this.bookCode);

        await this._getAllserverData();


        this._completeSyncLocalDataWithServer(false);
    }

    xapiSuccessCallback(event, { source, data }) {
        if (this.getActiveVM()) {
            this.xAPISuccessHandler(source, data);
        }
    }

    /**
     * function to sync local data and DB changes (dirty records) with server.
     * will fetch data from server and update all dirty records.
     */
    private _completeSyncLocalDataWithServer(insertToDB = true) {

        // return if the app is launched in offline mode.
        if (!this.isOnline) {
            return;
        }

        // get dirty records.
        let dirtyArray = this._getAllDirtyData();

        // make a copy of server data.
        let serverMap = this._getServerMapFromServerArray(this.serverData);

        let dirtyMap = this._getDirtyMapFromDirtyArray(dirtyArray);

        let toBePostedToServer = [];


        Object.keys(dirtyMap).forEach(key => {
            if (!serverMap[key]) {
                serverMap[key] = dirtyMap[key];
                toBePostedToServer.push(dirtyMap[key]);
            } else {
                if (serverMap[key].data.timestamp >= dirtyMap[key].data.timestamp) {
                    delete dirtyMap[key];
                } else {
                    serverMap[key] = dirtyMap[key];
                    toBePostedToServer.push(dirtyMap[key]);
                }
            }
        });

        toBePostedToServer.forEach(entity => {
            let id = entity.data.documentId.substring(3);
            let statement = entity.data.dirty.statement;
            this.xAPIHelperService.postStatmentToXAPI(id, [statement]);
        });

        // trigger pushNoBatch XAPI event.
        this.xAPIHelperService.flushStateAndStatement();
        this.xAPIHelperService.subscribe('success', this.xapiSuccessCallback);

        // seperate out server map to 3 arrays and buld insert them.

        if(insertToDB) {
            this._bulkInsertServerMapToDB(serverMap);
        }


        this.serverData = {
            notesData: [],
            drawingsData: [],
            preferencesData: []
        };
        
        this.serverCopy = undefined;

        // compare server data with dirty records.


        // find the changes to be pushed to server.

        // update server with dirty records.

        // on success, update DB and remove dirty records, server data
        // on failure, retry.


    }

    /**
     * get Local VM changes (dirty records) and push them to server. 
     * @param forceSync - to bypass the timer and sync forcefully.
     */
    public syncLocalWithServer(forceSync = false) {

        // return if the app is launched in offline mode.
        if (!this.isOnline || !this.getActiveVM()) {
            return;
        }


        if (forceSync) {

            console.log('forceSync');
            this.timerConfig.isTimerActive = false;
            clearTimeout(this.timerConfig.timerReference);
            this.timerConfig.timerReference = undefined;

            this._syncTimerFunction();

        }

        // if timer is running, then return.
        if (this.timerConfig.isTimerActive) {
            return;
        }
        this.timerConfig.isTimerActive = true;

        this.timerConfig.timerReference = setTimeout(() => {

            this._syncTimerFunction();

            this.timerConfig.isTimerActive = false;
            clearTimeout(this.timerConfig.timerReference);
            this.timerConfig.timerReference = undefined;

        }, this.timerConfig.timerDelay);


    }

    private _syncTimerFunction() {
        // parse VM, get dirty data.
        let dirtyArray = this._getAllDirtyData();

        dirtyArray.forEach(dirtyStatement => {
            // modify dirty data as required.
            let id = dirtyStatement.documentId.substring(3);
            let statement = dirtyStatement.dirty.statement;
            this.xAPIHelperService.postStatmentToXAPI(id, [statement]);
        });

        // trigger pushNoBatch XAPI event.
        this.xAPIHelperService.flushStateAndStatement();
    }

    /**
     * parses the local data to get dirty data.
     */
    private _getAllDirtyData() {

        let dirtyArray = [];
        Object.keys(this.localData).forEach(key => {
            this.localData[key].forEach(userData => {
                if (userData.dirty) {
                    dirtyArray.push(userData);
                }
            });
        });

        return dirtyArray;

    }

    /**
     * callback triggered when success response is returned from xAPI helper service after data has been posted.
     * @param source - source of success
     * @param data - success data
     */
    public xAPISuccessHandler(source, data) {

        // if success is recieved for data of other book, then return
        if (data.product.code != this.bookCode) {
            return;
        }

        let dirtyData = this._getAllDirtyData();
        data.entities.forEach(successData => {

            let foundIndex = -1;
            dirtyData.forEach((dirtData, index) => {
                if (dirtData.documentId.substring(3) == successData.user_data.documentid) {
                    foundIndex = index;
                }
            });

            if (foundIndex > -1) {

                let originalDirtyData = dirtyData[foundIndex];


                // meta field for created

                let finalData: any = {
                    folderId: successData.user_data.folderid,
                    documentId: "cup" + successData.user_data.documentid,
                    data: successData.user_data.payload,
                    timestamp: successData.timestamp,
                    meta: {
                        created: successData.timestamp
                    }
                }

                let type;
                if (originalDirtyData.timestamp > successData.timestamp) {
                    finalData.dirty = originalDirtyData.dirty;
                    finalData.timestamp = originalDirtyData.timestamp;
                    finalData.meta.created = successData.timestamp;
                } else {
                    dirtyData.splice(foundIndex, foundIndex + 1);
                }

                if (successData.user_data.type == 'note') {
                    if (originalDirtyData && originalDirtyData.dirty && originalDirtyData.dirty.userAction == 'delete') {
                        this.deleteNotesFromDB(this.userId, this.bookCode, finalData.documentId)
                    } else {
                        this.updateNoteInDB(this.userId, this.bookCode, finalData).then((res => {
                            type = DesktopEBookVMService.USER_DATA_TYPE.NOTES;
                            this._addDirtyDataToLocalData(type, finalData);
                        }));
                    }
                } else if (successData.user_data.type == 'annotation') {
                    if (originalDirtyData && originalDirtyData.dirty && originalDirtyData.dirty.userAction == 'delete') {
                        this.deleteDrawingsFromDB(this.userId, this.bookCode, finalData.documentId)
                    } else {
                        this.updateDrawingInDB(this.userId, this.bookCode, finalData).then((res => {
                            type = DesktopEBookVMService.USER_DATA_TYPE.DRAWINGS;
                            this._addDirtyDataToLocalData(type, finalData);
                        }));
                    }
                } else if (successData.user_data.type == 'settings-updated') {
                    this.updatePrefernecesInDB(this.userId, this.bookCode, finalData).then((res => {
                        type = DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE;
                        this._addDirtyDataToLocalData(type, finalData);
                    }));
                }

            }

        });

    }

    /**
     * returns a dirty map created from all the data recieved from dirty array.
     * @param dirtyArray 
     * @returns docID : { dataKey: notes/drawings/pref, data: actual data node.}
     */
    private _getDirtyMapFromDirtyArray(dirtyArray) {

        let dirtyMap = {};

        dirtyArray.forEach(dirtData => {
            let key = dirtData.dirty.statement.json['user_data'].type;
            let updatedKey;
            if (key == 'annotation') {
                updatedKey = DesktopEBookVMService.USER_DATA_TYPE.DRAWINGS;
            } else if (key == 'note') {
                updatedKey = DesktopEBookVMService.USER_DATA_TYPE.NOTES;
            } else if (key == 'settings-updated') {
                updatedKey = DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE;
            }

            dirtyMap[dirtData.documentId] = {
                dataKey: updatedKey,
                data: dirtData
            }
        });

        return dirtyMap;

    }

    /**
     * function to seperate server map into user data arrays and bulk insert data to DB.
     * @param serverMap 
     */
    private _bulkInsertServerMapToDB(serverMap) {

        let notesArray = [];
        let drawingsArray = [];
        let preferencesArray = [];

        Object.keys(serverMap).forEach(key => {
            if (serverMap[key].dataKey == DesktopEBookVMService.USER_DATA_TYPE.DRAWINGS) {
                if (serverMap[key].data && serverMap[key].data.dirty && serverMap[key].data.dirty.userAction == 'delete') {
                    this.deleteDrawingsFromDB(this.userId, this.bookCode, serverMap[key].data.documentId)
                } else {
                    drawingsArray.push(serverMap[key].data);
                }
            } else if (serverMap[key].dataKey == DesktopEBookVMService.USER_DATA_TYPE.NOTES) {
                if (serverMap[key].data && serverMap[key].data.dirty && serverMap[key].data.dirty.userAction == 'delete') {
                    this.deleteDrawingsFromDB(this.userId, this.bookCode, serverMap[key].data.documentId)
                } else {
                    notesArray.push(serverMap[key].data);
                }
            } else if (serverMap[key].dataKey == DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE) {
                preferencesArray.push(serverMap[key].data);
            }
        });

        let userNotesData = {
            "uid": this.userId,
            "ebookCode": this.bookCode,
            "data": notesArray
        }
        let userDrawingsData = {
            "uid": this.userId,
            "ebookCode": this.bookCode,
            "data": drawingsArray
        }
        let userPreferencesData = {
            "uid": this.userId,
            "ebookCode": this.bookCode,
            "data": preferencesArray
        }

        // add the lates data to local VM and do not wait to render view from DB data.
        this.localData.notesData = notesArray;
        this.localData.drawingsData = drawingsArray;
        this.localData.preferencesData = preferencesArray;

        Promise.all([
            this.desktopNotesDataService.setBulkNotes(userNotesData),
            this.desktopDrawingsDataService.setBulkDrawings(userDrawingsData),
            this.desktopUserPreferencesService.setBulkUserPreferences(userPreferencesData)
        ])


    }

    /**
     * returns a server map created from all the data recieved from server.
     * @param serverData 
     * @returns docID : { dataKey: notes/drawings/pref, data: actual data node.}
     */
    private _getServerMapFromServerArray(serverData) {

        let serverMap = {};

        Object.keys(serverData).forEach(key => {
            serverData[key].forEach(userData => {
                let updatedKey;

                if (key == 'drawingsData') {
                    updatedKey = DesktopEBookVMService.USER_DATA_TYPE.DRAWINGS;
                } else if (key == 'notesData') {
                    updatedKey = DesktopEBookVMService.USER_DATA_TYPE.NOTES;
                } else if (key == 'preferencesData') {
                    updatedKey = DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE;
                }
                serverMap[userData.documentId] = {
                    dataKey: updatedKey,
                    data: userData
                }
            });
        });

        return serverMap;

    }

    /**
     * function to fetch the user notes stored in DB.
     * used to render user view.
     * @param userId 
     * @param bookCode 
     * @param documentId 
     */
    public async getNotesFromDB(userId, bookCode, documentId?) {
        let params = {
            uid: userId,
            ebookCode: bookCode,
            documentId: documentId
        }
        return await this.desktopNotesDataService.getNotes(params);
    }

    /**
     * function to fetch the user drawings stored in DB.
     * used to render user view.
     * @param userId 
     * @param bookCode 
     * @param documentId 
     */
    public async getDrawingsFromDB(userId, bookCode, documentId?) {
        let params = {
            uid: userId,
            ebookCode: bookCode,
            documentId: documentId
        }
        return await this.desktopDrawingsDataService.getDrawings(params);
    }

    /**
     * function to fetch the user preferences stored in DB.
     * used to render user view.
     * @param userId 
     * @param bookCode 
     * @param documentId 
     */
    public async getPrefrencesFromDB(userId, bookCode, documentId?) {
        let params = {
            uid: userId,
            ebookCode: bookCode,
            documentId: documentId
        }
        return await this.desktopUserPreferencesService.getUserPreferences(params);
    }

    /**
     * function to fetch the user notes from server
     */
    public async getNotesFromServer() {
        let notesResponseFromServer
        try {
            let options = {
                productCode: this.bookCode,
                curser: undefined
            }
            notesResponseFromServer = await this.helperService.fetchNotes(options);
        } catch (err) {
            console.log("Error in fetching notes data: ", err);
        }

        return notesResponseFromServer;
    }

    /**
     * function to fetch the user drawings from server
     */
    public async getDrawingsFromServer() {
        let drawingsResponseFromServer;

        try {
            let options = {
                productCode: this.bookCode
            }
            drawingsResponseFromServer = await this.helperService.fetchDrawings(options);
        } catch (err) {
            console.log("Error in fetching drawings data: ", err);
        }

        return drawingsResponseFromServer;
    }

    /**
     * function to fetch the user preferences from server.
     */
    public async getPrefrencesFromServer() {
        let preferencesResponseFromServer;

        try {
            let options = {
                productCode: this.bookCode
            }
            preferencesResponseFromServer = await this.helperService.fetchPreferences(options);
        } catch (err) {
            console.log("Error in fetching preferences data: ", err);
        }
        return preferencesResponseFromServer;
    }

    /**
     * function to add / update if exists single note in DB
     * @param data - collection of notes data
     */
    public async updateNoteInDB(userId, bookCode, data) {
        let noteUpdateData = {
            uid: userId,
            ebookCode: bookCode,
            data: data
        }
        return await this.desktopNotesDataService.updateNote(noteUpdateData);
    }

    /**
     * function to add / update if exists single drawing in DB
     * @param data - collection of Drawings data
     * data - data / payload
     * documentId - 
     * folderId -
     * timestamp - 
     * statement -
     */
    public async updateDrawingInDB(userId, bookCode, data) {
        let drawingUpdateData = {
            uid: userId,
            ebookCode: bookCode,
            data: data
        }
        return await this.desktopDrawingsDataService.updateDrawing(drawingUpdateData);
    }

    /**
     * function to add / update if exists single preference in DB
     * @param data - collection of Preferences data
     */
    public async updatePrefernecesInDB(userId, bookCode, data) {
        let userPreferenceUpdateData = {
            uid: userId,
            ebookCode: bookCode,
            data: data
        }
        return await this.desktopUserPreferencesService.updateUserPreference(userPreferenceUpdateData);
    }

    /**
     * Function to delete notes from DB based on documentId
     * @param userId - unique user id
     * @param bookCode - unique book code
     * @param documentId - document id of deleted note
     */
    public async deleteNotesFromDB(userId, bookCode, documentId) {
        let drawingNoteData = {
            uid: userId,
            ebookCode: bookCode,
            documentId: documentId
        }
        return await this.desktopNotesDataService.deleteNotes(drawingNoteData);
    }

    /**
    * Function to delete drawings from DB based on documentId
    * @param userId - unique user id
    * @param bookCode - unique book code
    * @param documentId - document id of deleted note
    */
    public async deleteDrawingsFromDB(userId, bookCode, documentId) {
        let drawingDeleteData = {
            uid: userId,
            ebookCode: bookCode,
            documentId: documentId
        }
        return await this.desktopDrawingsDataService.deleteDrawings(drawingDeleteData);
    }

    /**
     * fetch all data from DB and store it in local memory.
     */
    private async _getAllDBData(userId, bookCode) {

        try {
            let repsonseArray = await Promise.all([
                this.getNotesFromDB(userId, bookCode),
                this.getDrawingsFromDB(userId, bookCode),
                this.getPrefrencesFromDB(userId, bookCode)
            ]);

            this.localData.notesData = repsonseArray[0];
            this.localData.drawingsData = repsonseArray[1];
            this.localData.preferencesData = repsonseArray[2];
            return true;
        } catch (e) {
            console.log(e);
        }

    }

    /**
     * function to get all user data for this book from server.
     */
    private async _getAllserverData() {

        try {
            let repsonseArray = await Promise.all([
                this.getNotesFromServer(),
                this.getDrawingsFromServer(),
                this.getPrefrencesFromServer()
            ]);

            this.serverData.notesData = repsonseArray[0];
            this.serverData.drawingsData = repsonseArray[1];
            this.serverData.preferencesData = repsonseArray[2];
            return true;
        } catch (e) {
            console.log(e);
        }

    }

    /**
     * function to add new dirty record in VM
     * @param type - type of dirty record (notes, drawings, preferences)
     * @param dirtyData - the actual data (id, statement, userData)
     * @param id - document id,
     * @param statement - generated statement
     * @param userData - original user data 
     */
    public addNewDirtyRecord(type, dirtyData) {

        // insert dirty records to DB
        dirtyData.statement.forEach(dStatemnt => {
            let timestamp = new Date(dStatemnt.json.timestamp).getTime();
            let data = {
                folderId: dirtyData.userData.folderid,
                documentId: "cup" + dirtyData.userData.documentid,
                data: dirtyData.userData.payload,
                timestamp: timestamp,
                dirty: {
                    userAction: dirtyData.userData.action,
                    statement: dStatemnt
                },
                meta: {
                    created: timestamp
                }
            }

            if (type == DesktopEBookVMService.USER_DATA_TYPE.NOTES) {
                this.updateNoteInDB(this.userId, this.bookCode, data);
            } else if (type == DesktopEBookVMService.USER_DATA_TYPE.DRAWINGS) {
                this.updateDrawingInDB(this.userId, this.bookCode, data);
            } else if (type == DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE) {
                this.updatePrefernecesInDB(this.userId, this.bookCode, data);
            }

            // add dirty records to VM
            this._addDirtyDataToLocalData(type, data);

        });


        // trigger event based sync here
        this.syncLocalWithServer();

    }

    /**
     * parse the local data and add dirty data to it.
     * @param type
     * @param data - actual data as added to DB. data contains -
     * @param folderId: dirtyData.userData.folderid,
     * @param documentId: dirtyData.userData.documentid,
     * @param data: dirtyData.userData.payload,
     * @param timestamp: timestamp in number,
     * @param dirty: { statement: statement }
    */
    private _addDirtyDataToLocalData(type, data) {

        if (type == DesktopEBookVMService.USER_DATA_TYPE.NOTES) {

            let foundIndex = -1;
            this.localData.notesData.forEach((noteData, index) => {
                if (foundIndex == -1 && (noteData.documentId == data.documentId || noteData.documentId.substring(3) == data.documentId)) {
                    foundIndex = index;
                }
            });

            if (foundIndex > -1) {
                this.localData.notesData[foundIndex] = data;
            } else {
                this.localData.notesData.push(data);
            }

        } else if (type == DesktopEBookVMService.USER_DATA_TYPE.DRAWINGS) {

            let foundIndex = -1;
            this.localData.drawingsData.forEach((drawingData, index) => {
                if (foundIndex == -1 && (drawingData.documentId == data.documentId || drawingData.documentId.substring(3) == data.documentId)) {
                    foundIndex = index;
                }
            });

            if (foundIndex > -1) {
                this.localData.drawingsData[foundIndex] = data;
            } else {
                this.localData.drawingsData.push(data);
            }

        } else if (type == DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE) {

            let foundIndex = -1;
            this.localData.preferencesData.forEach((preferenceData, index) => {
                if (foundIndex == -1 && (preferenceData.documentId == data.documentId || preferenceData.documentId.substring(3) == data.documentId)) {
                    foundIndex = index;
                }
            });

            if (foundIndex > -1) {
                this.localData.preferencesData[foundIndex] = data;
            } else {
                this.localData.preferencesData.push(data);
            }

        }

    }

    public getLocalData() {
        return this.localData;
    }

}
